import {useQuery, useQueryClient} from 'react-query'
import {getRewardAvailability} from 'api'
import {getItemFromStorage} from 'utils/storage'
import {COIN_BALANCE_QUERY_KEY} from 'hooks/useCoinBalance'
import {REWARD_NOTIFICATIONS_QUERY_KEY} from 'hooks/useRewardNotifications'

/**
 * Per-trigger "can I still earn this?" for the signed-in member, used
 * to decorate reward-triggering buttons (post, comment) with a coin
 * hint. Returns {} while loading or signed out, so callers can index
 * into it unconditionally.
 *
 * Availability is a UI hint only — the backend re-checks every gate at
 * award time — so a shared 5-minute cache is plenty. Mutations that can
 * consume an award (posting, commenting) should call
 * useInvalidateRewardState so a just-hit cap hides the hint and the
 * header balance ticks up.
 */
export const REWARD_AVAILABILITY_QUERY_KEY = 'rewardAvailability'

// The backend awards post/comment coins fire-and-forget, after the
// create response has already been sent. A refetch fired the instant
// that response lands can beat the award row into the database and
// come back saying the trigger is still available (and the balance
// unchanged, and no popup pending). So invalidate now for the common
// case, and once more after the award has had time to settle.
const SETTLE_DELAY_MS = 2500

const useRewardAvailability = () => {
  const user = getItemFromStorage({name: 'user'})
  const {data} = useQuery(
    REWARD_AVAILABILITY_QUERY_KEY,
    getRewardAvailability,
    {
      enabled: !!user?.id,
      staleTime: 5 * 60 * 1000,
    }
  )
  return data?.availability || {}
}

export const useInvalidateRewardState = () => {
  const queryClient = useQueryClient()
  return () => {
    const invalidate = () => {
      queryClient.invalidateQueries(REWARD_AVAILABILITY_QUERY_KEY)
      queryClient.invalidateQueries(COIN_BALANCE_QUERY_KEY)
      // ...and the popup itself, so "You earned N Sharpennies!" shows
      // where the member is instead of on the next page load.
      queryClient.invalidateQueries(REWARD_NOTIFICATIONS_QUERY_KEY)
    }
    invalidate()
    setTimeout(invalidate, SETTLE_DELAY_MS)
  }
}

export default useRewardAvailability
